import { Cable, ChevronRight, Code, Database, GitMerge, Puzzle, Zap } from "lucide-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { ChatInterface } from "../components/chat/ChatInterface";
import { FeatureCard, ProductCard } from "../components/features/FeatureCard";
import { Footer } from "../components/layout/Footer";
import { Navbar } from "../components/layout/Navbar";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../hooks/useTranslation";

const Index = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Initialize fade-in animations
  useEffect(() => {
    const fadeElements = document.querySelectorAll(".fade-in-section");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    fadeElements.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      fadeElements.forEach((element) => {
        observer.unobserve(element);
      });
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (!section) return;

    const headerOffset = 80; // 与导航栏高度一致
    const elementPosition = section.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  return (
    <>
      <Helmet>
        <title>{t("meta.home.title")}</title>
        <meta name="description" content={t("meta.home.description")} />
        <meta name="keywords" content={t("meta.home.keywords")} />
        <meta property="og:title" content={t("meta.home.title")} />
        <meta property="og:description" content={t("meta.home.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://gugudata.com" />
        <link rel="canonical" href="https://gugudata.com" />
        <html lang={language} />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow">
          {/* Hero Section */}
          <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-white dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="fade-in-section">
                <div className="text-center mb-16">
                  <h1 className="text-4xl md:text-6xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">{t("hero.title")}</h1>
                  <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-5xl mx-auto mb-8">{t("hero.subtitle")}</p>
                  <button
                    onClick={() => scrollToSection("chat")}
                    className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-brand-blue rounded-full shadow-lg hover:bg-opacity-90 transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1"
                  >
                    {t("hero.cta")} <ChevronRight className="ml-2 h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Chat Interface */}
              <div id="chat" className="fade-in-section">
                <ChatInterface />
              </div>
            </div>
          </section>

          {/* Features Section */}
          <section id="features" className="py-24 bg-gray-50 dark:bg-gray-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="fade-in-section">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t("features.title")}</h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">{t("features.subtitle")}</p>
                </div>

                <div className="grid md:grid-cols-3 gap-8">
                  <FeatureCard icon={<GitMerge className="w-8 h-8" />} title={t("features.card1.title")} description={t("features.card1.description")} />
                  <FeatureCard icon={<Cable className="w-8 h-8" />} title={t("features.card2.title")} description={t("features.card2.description")} />
                  <FeatureCard icon={<Database className="w-8 h-8" />} title={t("features.card3.title")} description={t("features.card3.description")} />
                </div>

                <div className="mt-16 grid md:grid-cols-2 gap-8">
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 transition-all duration-300 hover:shadow-2xl">
                    <div className="flex items-start mb-6">
                      <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg mr-4">
                        <Code className="w-8 h-8 text-brand-blue" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{t("features.standardized.title")}</h3>
                        <p className="text-gray-600 dark:text-gray-300">{t("features.standardized.description")}</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-gray-600 dark:text-gray-300 ml-4">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.standardized.list.item1")}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.standardized.list.item2")}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.standardized.list.item3")}</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 transition-all duration-300 hover:shadow-2xl">
                    <div className="flex items-start mb-6">
                      <div className="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 p-3 rounded-lg mr-4">
                        <Puzzle className="w-8 h-8 text-brand-blue" />
                      </div>
                      <div>
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{t("features.modular.title")}</h3>
                        <p className="text-gray-600 dark:text-gray-300">{t("features.modular.description")}</p>
                      </div>
                    </div>
                    <ul className="space-y-3 text-gray-600 dark:text-gray-300 ml-4">
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.modular.list.item1")}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.modular.list.item2")}</span>
                      </li>
                      <li className="flex items-center">
                        <span className="w-2 h-2 bg-brand-blue rounded-full mr-2"></span>
                        <span>{t("features.modular.list.item3")}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Products Section */}
          <section id="products" className="py-24 bg-white dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="fade-in-section">
                <div className="text-center mb-16">
                  <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900 dark:text-white">{t("products.title")}</h2>
                  <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">{t("products.subtitle")}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-12">
                  <ProductCard
                    icon={<Zap className="w-full h-full" />}
                    title={t("products.card1.title")}
                    description={t("products.card1.description")}
                    ctaText={t("products.card1.cta")}
                    ctaLink="/document"
                  />
                  <ProductCard icon={<Database className="w-full h-full" />} title={t("products.card2.title")} description={t("products.card2.description")} ctaText={t("products.card2.cta")} />
                </div>

                {/* MCP Integration Process */}
                <div className="mt-24">
                  <h3 className="text-2xl font-bold text-center mb-12 text-gray-900 dark:text-white">{t("features.integration.title")}</h3>
                  <div className="relative">
                    <div className="absolute top-1/2 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-blue to-transparent transform -translate-y-1/2 opacity-20" />
                    <div className="grid md:grid-cols-3 gap-8 relative">
                      {[
                        {
                          step: 1,
                          title: t("features.integration.step1.title"),
                          description: t("features.integration.step1.description"),
                        },
                        {
                          step: 2,
                          title: t("features.integration.step2.title"),
                          description: t("features.integration.step2.description"),
                        },
                        {
                          step: 3,
                          title: t("features.integration.step3.title"),
                          description: t("features.integration.step3.description"),
                        },
                      ].map((item, index) => (
                        <div key={item.step} className="relative bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg group hover:shadow-xl transition-all duration-300">
                          <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-brand-blue to-blue-400 rounded-xl flex items-center justify-center text-white font-bold text-lg transform group-hover:scale-110 transition-transform duration-300">
                            {item.step}
                          </div>
                          <div className="mt-4">
                            <h4 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">{item.title}</h4>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed">{item.description}</p>
                          </div>
                          {index < 2 && (
                            <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 text-brand-blue">
                              <ChevronRight className="w-8 h-8 opacity-50" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 bg-brand-blue">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-bold text-white mb-6">{t("features.cta.title")}</h2>
              <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">{t("features.cta.description")}</p>
              <button
                onClick={() => scrollToSection("chat")}
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-brand-blue bg-white rounded-full shadow-lg hover:bg-gray-100 transition-all duration-300 hover:shadow-xl"
              >
                {t("hero.cta")} <ChevronRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Index;
